import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Phone, Mail, Trash2, Siren, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function EmergencyPage() {
  const { t } = useI18n();
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", relationship: "", phone: "", email: "", access_allowed: false, notes: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/emergency-contacts");
      setContacts(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/emergency-contacts", form);
      setContacts((arr) => [...arr, data]);
      setOpen(false);
      setForm({ name: "", relationship: "", phone: "", email: "", access_allowed: false, notes: "" });
      toast.success("Notfallkontakt gespeichert.");
    } catch {
      toast.error("Konnte Kontakt nicht speichern");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Diesen Kontakt wirklich entfernen?")) return;
    try {
      await api.delete(`/emergency-contacts/${id}`);
      setContacts((arr) => arr.filter((c) => c.id !== id));
      toast.success("Kontakt entfernt.");
    } catch {
      toast.error("Konnte Kontakt nicht entfernen");
    }
  };

  return (
    <AppLayout>
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2 inline-flex items-center gap-2">
            <Siren className="h-3 w-3" /> {t.nav.emergency}
          </div>
          <h1 className="font-serif text-5xl text-ag-ink mb-2">{t.emergency.title}</h1>
          <p className="text-slate-600 max-w-2xl">{t.emergency.subtitle}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-ag-ink hover:bg-slate-800 h-11 rounded-xl" data-testid="emergency-add-btn">
              <Plus className="h-4 w-4 mr-2" /> {t.emergency.addContact}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{t.emergency.addContact}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2" data-testid="emergency-form">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.name}</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emergency-form-name" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.relationship}</Label>
                <Input required value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} data-testid="emergency-form-rel" className="mt-1.5 h-11" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.phone}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emergency-form-phone" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.email}</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emergency-form-email" className="mt-1.5 h-11" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.notes}</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-ag-ink">{t.emergency.access}</div>
                  <div className="text-xs text-slate-500">Diese Person darf Ihre Daten im Notfall einsehen.</div>
                </div>
                <Switch checked={form.access_allowed} onCheckedChange={(v) => setForm({ ...form, access_allowed: v })} data-testid="emergency-form-access" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{t.emergency.cancel}</Button>
                <Button type="submit" className="bg-ag-ink hover:bg-slate-800" data-testid="emergency-form-submit">{t.emergency.save}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {contacts.length === 0 ? (
        <div className="ag-card p-12 text-center" data-testid="emergency-empty">
          <div className="h-14 w-14 rounded-2xl bg-slate-100 mx-auto flex items-center justify-center mb-4">
            <Siren className="h-6 w-6 text-slate-500" strokeWidth={1.4} />
          </div>
          <p className="text-slate-600 max-w-md mx-auto">{t.emergency.empty}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {contacts.map((c) => (
            <div key={c.id} className="ag-card p-6" data-testid={`emergency-contact-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center text-ag-ink font-serif text-lg">
                  {c.name?.charAt(0)?.toUpperCase()}
                </div>
                <span className={`ag-badge ${c.access_allowed ? "ag-badge-green" : "ag-badge-slate"}`}>
                  <ShieldCheck className="h-3 w-3" /> {c.access_allowed ? t.emergency.access : t.emergency.noAccess}
                </span>
              </div>
              <div className="font-serif text-xl text-ag-ink">{c.name}</div>
              <div className="text-xs text-slate-500 mb-3">{c.relationship}</div>
              {c.phone && <div className="text-sm text-slate-700 flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /> {c.phone}</div>}
              {c.email && <div className="text-sm text-slate-700 flex items-center gap-2 mt-1"><Mail className="h-3.5 w-3.5 text-slate-400" /> {c.email}</div>}
              {c.notes && <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-3 line-clamp-3">{c.notes}</p>}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`emergency-delete-${c.id}`}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-slate-500 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="h-3 w-3" /> {t.emergency.delete}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
